import { statusFilterOrder, statusPillClass } from '@/components/status';
import type { SubscriptionRunStatus } from '@/types';

interface Props {
  counts: ReadonlyMap<SubscriptionRunStatus, number>;
  total: number;
  selected: ReadonlySet<SubscriptionRunStatus>;
  onToggle: (status: SubscriptionRunStatus) => void;
  onClear: () => void;
}

/**
 * Multi-select status chips — Failed and Unknown together is a realistic triage
 * view. An empty selection means "show everything", so the page opens unfiltered
 * and the All chip doubles as the reset.
 */
export function StatusFilterBar({ counts, total, selected, onToggle, onClear }: Props) {
  // Only offer statuses the estate actually contains, plus any already selected so
  // an active filter can't vanish when its last matching row does.
  const available = statusFilterOrder.filter(s => (counts.get(s) ?? 0) > 0 || selected.has(s));
  if (available.length < 2 && selected.size === 0) return null;

  return (
    <div className="status-filters" role="group" aria-label="Filter by status">
      <button
        type="button"
        className={`pill status-filter status-filter-all${selected.size === 0 ? ' on' : ''}`}
        aria-pressed={selected.size === 0}
        onClick={onClear}
      >
        All<span className="count">{total}</span>
      </button>
      {available.map(status => {
        const on = selected.has(status);
        return (
          <button
            key={status}
            type="button"
            className={`pill status-filter ${statusPillClass(status)}${on ? ' on' : ''}`}
            aria-pressed={on}
            onClick={() => onToggle(status)}
          >
            {status}<span className="count">{counts.get(status) ?? 0}</span>
          </button>
        );
      })}
    </div>
  );
}
